// @flow

import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import isNaN from 'lodash/isNaN';
import {
  FilterItem,
  FilterSelect,
  FilterElementColor,
  FilterElementInitial,
  FilterElementOrientation,
  FilterElementGender,
  FilterColorPalette,
  FilterOption,
  FilterOptionsList as FilterOptions,
} from '@riseart/filter';
import { MediaQuery } from '@riseart/common';
import { DIMENSIONS, convertUnits } from '@riseart/filter-service';
import { useSelector, shallowEqual } from 'react-redux';
import { useLocale } from 'shared_services/redux/hooks/useLocale';
import { selectUnitSystem } from 'shared_services/redux/selectors/unitSystem';
import { formatCurrency } from 'shared_services/riseart/utils/Utils';
import { FilterSlider } from 'shared_hocs/sliders/Filter';
import { useStoreCode } from 'shared_services/redux/hooks/useStoreCode';
import { application as CONFIG_APP } from 'Config';
import { filter as FILTER_ENUM, units as ENUM_UNITS } from 'Enum';

import { raScreenSmMax, raScreenMd } from '@riseart/antd-provider/dist/website/variables.less';

/**
 * priceTipFormatter
 *
 * @param {Object} dataProps
 * @returns {Function}
 */
const priceTipFormatter =
  (dataProps: Object): Function =>
  (item) => {
    const { value, helper } = dataProps.options[item] || {};

    return isNaN(parseInt(value, 10))
      ? helper
      : formatCurrency(value, dataProps.storeCode, undefined, {
          precision: 0,
          locale: dataProps.locale && dataProps.locale.name,
        });
  };

/**
 * sizeTipFormatter
 *
 * @param {object} dataProps
 * @returns {Function}
 */
const sizeTipFormatter =
  (dataProps: Object): Function =>
  (item) => {
    const { config, translationDomain, value } =
      (dataProps && dataProps.options && dataProps.options[item]) || {};
    const unitLength = dataProps.unitLength && dataProps.unitLength.toLowerCase();

    if (!config || !value) {
      return null;
    }

    return (
      <FormattedMessage
        id={`filters.${translationDomain}.${dataProps.dimension}.${value}.helper`}
        values={{
          value: (
            <FormattedMessage
              id={unitLength === ENUM_UNITS.inch.long ? `common.unitIn` : `common.unitCm`}
              values={{
                value:
                  config.translationValues && config.translationValues[value]
                    ? Math.round(
                        convertUnits(
                          parseInt(config.translationValues[value], 10),
                          ENUM_UNITS.centimeter.short,
                          unitLength,
                        ),
                      )
                    : value,
              }}
            >
              {(value: Array<string>) => value}
            </FormattedMessage>
          ),
        }}
      >
        {(label) => label}
      </FormattedMessage>
    );
  };

/**
 * dimensionTipFormatter
 *
 * @param {Object} dataProps
 * @returns {Function}
 */
const dimensionTipFormatter =
  (dataProps: Object): Function =>
  (item) => {
    const { value, helper } = dataProps.options[item] || {};

    return isNaN(parseInt(value, 10)) ? (
      helper
    ) : (
      <FormattedMessage
        id={
          dataProps.unitLength && dataProps.unitLength.toLowerCase() === ENUM_UNITS.inch.long
            ? 'common.unitIn'
            : 'common.unitCm'
        }
        values={{
          value: value / 10,
        }}
      >
        {(value: string) => value}
      </FormattedMessage>
    );
  };

/**
 * priceRangeFormatter
 *
 * @param {Array<Object>} selectedValues
 * @param {Object} props
 * @returns {?Node}
 */
const priceRangeFormatter = (selectedValues: Array<Object>, props: Object): ?Node => {
  if (!selectedValues || selectedValues.length < 2) {
    return null;
  }

  const [from, to] = selectedValues;
  const lastMaxValueItem = props.options[props.options.length - 2];
  const min =
    from &&
    from.value &&
    formatCurrency(from.value, undefined, undefined, {
      precision: 0,
      hideCurrencyCode: true,
      locale: props.locale && props.locale.name,
    });
  const max =
    to && to.value === 'UP' && lastMaxValueItem && lastMaxValueItem.value
      ? `${formatCurrency(lastMaxValueItem.value, undefined, undefined, {
          precision: 0,
          hideCurrencyCode: true,
          locale: props.locale && props.locale.name,
        })}+`
      : (to &&
          to.value &&
          formatCurrency(to.value, undefined, undefined, {
            precision: 0,
            hideCurrencyCode: true,
            locale: props.locale && props.locale.name,
          })) ||
        null;

  return (
    <FormattedMessage
      id="components.filters.rangePrice"
      values={{ min, max, currency: CONFIG_APP.i18n.currency.codes[props.countryCode] }}
    >
      {(value: Array<string>) => value}
    </FormattedMessage>
  );
};

/**
 * dimensionRangeFormatter
 *
 * @param {Array<Object>} selectedValues
 * @param {Object} props
 * @returns {?Node}
 */
const dimensionRangeFormatter = (selectedValues: Array<Object>, props: Object): ?Node => {
  if (!selectedValues || selectedValues.length < 2) {
    return null;
  }

  const [from, to] = selectedValues;
  const lastMaxValueItem = props.options[props.options.length - 2];
  const min = from && from.value && from.value / 10;
  const max =
    to && to.value === 'UP' && lastMaxValueItem && lastMaxValueItem.value
      ? `${lastMaxValueItem.value / 10}+`
      : (to && to.value && to.value / 10) || null;

  return (
    <FormattedMessage
      id="components.filters.rangeUnits"
      values={{
        min,
        max,
        units:
          props.unitLength && props.unitLength.toLowerCase() === ENUM_UNITS.inch.long
            ? ENUM_UNITS.inch.short
            : ENUM_UNITS.centimeter.short,
      }}
    >
      {(value: Array<string>) => value}
    </FormattedMessage>
  );
};

type Props = {
  translations: Object,
  options: Object,
  listSelectionHandler: Function,
  rangeSelectionHandler: Function,
  showLabel?: boolean,
  inlineLabel?: boolean,
  marks?: boolean,
  align?: 'left' | 'right',
  dimension: string,
  formatMessage: Function,
  unitLength?: string,
};

const COMPONENT_RENDERERS = {
  orientation: ({
    formatMessage,
    options,
    listSelectionHandler,
    showLabel = true,
    dimension,
  }: Props) => (
    <FilterItem title={showLabel && formatMessage({ id: `components.filters.${dimension}.label` })}>
      <FilterOptions className={dimension}>
        {options &&
          options.map((option) => (
            <FilterElementOrientation
              key={option.url}
              {...option}
              showHint
              dimension={dimension}
              handleClick={listSelectionHandler}
            />
          ))}
      </FilterOptions>
    </FilterItem>
  ),
  slider:
    (tipFormatter, rangeFormatter) =>
    ({
      formatMessage,
      options,
      rangeSelectionHandler,
      showLabel = true,
      marks = true,
      unitLength,
      dimension,
    }: Props) => {
      const locale = useLocale();
      const storeCode = useStoreCode();

      return (
        <FilterSlider
          title={showLabel && formatMessage({ id: `components.filters.${dimension}.label` })}
          marks={marks}
          options={options}
          dimension={dimension}
          onChange={rangeSelectionHandler}
          tipFormatter={tipFormatter}
          rangeFormatter={rangeFormatter}
          unitLength={unitLength}
          locale={locale}
          type="inline"
          storeCode={storeCode}
        />
      );
    },
  colorSelect: ({
    formatMessage,
    options,
    listSelectionHandler,
    align,
    showLabel,
    inlineLabel,
    dimension,
  }: Props) => {
    const label = formatMessage({ id: `components.filters.${dimension}.label` });

    return (
      <FilterItem title={showLabel ? label : null}>
        <FilterSelect
          multiple
          border={false}
          align={align}
          labels={{
            defaultText: showLabel ? (
              formatMessage({ id: `components.filters.${dimension}.defaultText` })
            ) : (
              <FilterColorPalette>
                {inlineLabel ? <strong>{label.toUpperCase()}</strong> : null}
              </FilterColorPalette>
            ),
            multipleValues: formatMessage({
              id: `components.filters.${dimension}.multiple`,
            }),
            listTitle: formatMessage({ id: `components.filters.${dimension}.title` }),
            noOptions: formatMessage({ id: `components.filters.${dimension}.noOptions` }),
            clearAll: formatMessage({ id: `components.filters.clearAll` }),
            selectedCount: formatMessage({
              id: `components.filters.selectedCount`,
            }),
          }}
          onChange={listSelectionHandler}
          dimension={dimension}
        >
          {options
            ? options.map((item, key) => <FilterElementColor key={key} labeled {...item} />)
            : null}
        </FilterSelect>
      </FilterItem>
    );
  },
  color: ({
    formatMessage,
    options,
    listSelectionHandler,
    showLabel = true,
    align,
    dimension,
  }: Props) => {
    const label = showLabel && formatMessage({ id: `components.filters.${dimension}.label` });

    return (
      <MediaQuery minWidth="0">
        <MediaQuery maxWidth={raScreenSmMax}>
          <FilterItem title={label}>
            <FilterSelect
              multiple
              align={align}
              labels={{
                defaultText: formatMessage({
                  id: `components.filters.${dimension}.defaultText`,
                }),
                multipleValues: formatMessage({
                  id: `components.filters.${dimension}.multiple`,
                }),
                listTitle: formatMessage({ id: `components.filters.${dimension}.title` }),
                noOptions: formatMessage({ id: `components.filters.${dimension}.noOptions` }),
                clearAll: formatMessage({ id: `components.filters.clearAll` }),
                selectedCount: formatMessage({
                  id: `components.filters.selectedCount`,
                }),
              }}
              onChange={listSelectionHandler}
              dimension={dimension}
            >
              {options
                ? options.map((item, key) => <FilterElementColor key={key} labeled {...item} />)
                : null}
            </FilterSelect>
          </FilterItem>
        </MediaQuery>
        <MediaQuery minWidth={raScreenMd}>
          <FilterItem faded title={label}>
            <FilterOptions className={dimension}>
              {options &&
                options.map((option) => (
                  <FilterElementColor
                    key={option.url}
                    {...option}
                    showHint
                    dimension={dimension}
                    handleClick={listSelectionHandler}
                  />
                ))}
            </FilterOptions>
          </FilterItem>
        </MediaQuery>
      </MediaQuery>
    );
  },
  gender: ({
    dimension,
    options,
    listSelectionHandler,
    showLabel = true,
    formatMessage,
  }: Props) => (
    <FilterItem title={showLabel && formatMessage({ id: `components.filters.${dimension}.label` })}>
      <FilterOptions className={dimension}>
        {options &&
          options.map((option) => (
            <FilterElementGender
              key={option.url}
              {...option}
              showHint
              hintLabel={formatMessage({ id: `common.gender.${option.value.toUpperCase(0)}` })}
              dimension={dimension}
              handleClick={listSelectionHandler}
            >
              {option.value}
            </FilterElementGender>
          ))}
      </FilterOptions>
    </FilterItem>
  ),
  initial: ({
    dimension,
    options,
    listSelectionHandler,
    showLabel,
    align,
    formatMessage,
  }: Props) => {
    // Get all available options and not just returned by query as available
    const availableOptions = options.reduce(
      (accumulator, option) => ({ ...accumulator, [option.value]: option }),
      {},
    );
    const label = formatMessage({ id: `components.filters.${dimension}.label` });

    return (
      <FilterItem title={showLabel ? label : null}>
        <FilterSelect
          inline
          multiple
          hideSummary
          showAllOptions
          align={align}
          labels={{
            defaultText: showLabel ? (
              formatMessage({ id: `components.filters.${dimension}.defaultText` })
            ) : (
              <strong>{label.toUpperCase()}</strong>
            ),
            multipleValues: formatMessage({ id: `components.filters.${dimension}.multiple` }),
            listTitle: formatMessage({ id: `components.filters.${dimension}.title` }),
            noOptions: formatMessage({ id: `components.filters.${dimension}.noOptions` }),
            clearAll: formatMessage({ id: `components.filters.clearAll` }),
            selectedCount: formatMessage({ id: `components.filters.selectedCount` }),
          }}
          onChange={listSelectionHandler}
          dimension={dimension}
        >
          {Object.keys(DIMENSIONS.values.initials).map((letter, key) => {
            // Available options which exist in filter selection list returned by query
            const option = availableOptions[letter];

            return (
              <FilterElementInitial
                key={key}
                disabled={!option}
                {...(option || { label: letter, value: letter })}
              />
            );
          })}
        </FilterSelect>
      </FilterItem>
    );
  },
  dropdown: ({
    dimension,
    options,
    showLabel,
    listSelectionHandler,
    align,
    formatMessage,
  }: Props) => {
    const label = formatMessage({ id: `components.filters.${dimension}.label` });

    return (
      <FilterItem title={showLabel ? label : null}>
        <FilterSelect
          multiple
          align={align}
          labels={{
            defaultText: showLabel ? (
              formatMessage({ id: `components.filters.${dimension}.defaultText` })
            ) : (
              <strong>{label.toUpperCase()}</strong>
            ),
            multipleValues: formatMessage({ id: `components.filters.${dimension}.multiple` }),
            listTitle: formatMessage({ id: `components.filters.${dimension}.title` }),
            noOptions: formatMessage({ id: `components.filters.${dimension}.noOptions` }),
            clearAll: formatMessage({ id: `components.filters.clearAll` }),
            selectedCount: formatMessage({ id: `components.filters.selectedCount` }),
          }}
          onChange={listSelectionHandler}
          dimension={dimension}
        >
          {options ? options.map((item, key) => <FilterOption key={key} {...item} />) : null}
        </FilterSelect>
      </FilterItem>
    );
  },
};

const COMPONENT_BY_DOMAIN = {
  [FILTER_ENUM.domain.ART]: {
    medium: COMPONENT_RENDERERS.dropdown,
    style: COMPONENT_RENDERERS.dropdown,
    subject: COMPONENT_RENDERERS.dropdown,
    orientation: COMPONENT_RENDERERS.orientation,
    price: COMPONENT_RENDERERS.slider(priceTipFormatter, priceRangeFormatter),
    size: COMPONENT_RENDERERS.slider(sizeTipFormatter),
    color: COMPONENT_RENDERERS.colorSelect,
    ships: COMPONENT_RENDERERS.dropdown,
    width: COMPONENT_RENDERERS.slider(dimensionTipFormatter, dimensionRangeFormatter),
    height: COMPONENT_RENDERERS.slider(dimensionTipFormatter, dimensionRangeFormatter),
  },
  [FILTER_ENUM.domain.ARTIST]: {
    medium: COMPONENT_RENDERERS.dropdown,
    style: COMPONENT_RENDERERS.dropdown,
    initial: COMPONENT_RENDERERS.initial,
    gender: COMPONENT_RENDERERS.gender,
    ships: COMPONENT_RENDERERS.dropdown,
    stage: COMPONENT_RENDERERS.dropdown,
    region: COMPONENT_RENDERERS.dropdown,
    born: COMPONENT_RENDERERS.dropdown,
  },
  [FILTER_ENUM.domain.ARTIST_ART]: {
    orientation: COMPONENT_RENDERERS.orientation,
    price: COMPONENT_RENDERERS.slider(priceTipFormatter, priceRangeFormatter),
    size: COMPONENT_RENDERERS.slider(sizeTipFormatter),
    width: COMPONENT_RENDERERS.slider(dimensionTipFormatter, dimensionRangeFormatter),
    height: COMPONENT_RENDERERS.slider(dimensionTipFormatter, dimensionRangeFormatter),
  },
  [FILTER_ENUM.domain.COLLECTION]: {
    medium: COMPONENT_RENDERERS.dropdown,
    style: COMPONENT_RENDERERS.dropdown,
    subject: COMPONENT_RENDERERS.dropdown,
  },
  [FILTER_ENUM.domain.COLLECTION_ART]: {
    orientation: COMPONENT_RENDERERS.orientation,
    price: COMPONENT_RENDERERS.slider(priceTipFormatter, priceRangeFormatter),
    size: COMPONENT_RENDERERS.slider(sizeTipFormatter),
    width: COMPONENT_RENDERERS.slider(dimensionTipFormatter, dimensionRangeFormatter),
    height: COMPONENT_RENDERERS.slider(dimensionTipFormatter, dimensionRangeFormatter),
  },
  [FILTER_ENUM.domain.FAVORITES_ART]: {
    medium: COMPONENT_RENDERERS.dropdown,
    subject: COMPONENT_RENDERERS.dropdown,
    price: COMPONENT_RENDERERS.slider(priceTipFormatter, priceRangeFormatter),
    size: COMPONENT_RENDERERS.slider(sizeTipFormatter),
  },
  [FILTER_ENUM.domain.FAVORITES_ARTIST]: {
    medium: COMPONENT_RENDERERS.dropdown,
    initial: COMPONENT_RENDERERS.initial,
    born: COMPONENT_RENDERERS.dropdown,
    ships: COMPONENT_RENDERERS.dropdown,
  },
};

export function FilterItemBuilder({
  domain,
  dimension,
  type,
  defaultOptionsList,
  sort,
  options,
  ...restProps
}: Object) {
  const { formatMessage } = useIntl();
  const { unitLength } = useSelector(selectUnitSystem, shallowEqual);
  const Component = COMPONENT_BY_DOMAIN[domain][dimension];

  if (!Component) {
    throw new Error(
      `[FilterBuilder] Unknown filter component type for domain ${domain} and dimension ${dimension}`,
    );
  }

  return (
    <Component
      {...restProps}
      formatMessage={formatMessage}
      dimension={dimension}
      options={typeof sort === 'function' ? options[dimension].sort(sort) : options[dimension]}
      unitLength={unitLength}
    />
  );
}
